'use client'

import { cn } from '@/lib/utils'
import { BEFORE_EARTHQUAKE_LABEL, fmtElapsed } from '@/lib/elapsed'

export interface ElapsedTimeProps {
  /** Story minute since the earthquake; negative means before it. Null renders nothing. */
  min: number | null | undefined
  variant?: 'text' | 'pill'
  /** A label the server already formatted ("+16 h 20 min"); wins over formatting `min`. */
  label?: string | null
  /** Text before the time in the pill, for example the event id ("EQ-2026-A"). */
  prefix?: string | null
  /** Tooltip, for example the story step ("Step 8a: Supplies by mode"). */
  title?: string | null
  className?: string
}

/**
 * A story time (plans/03 A5.5). The pill is the header clock: coral dot after
 * the earthquake, grey dot and "Before the earthquake" before it.
 */
export function ElapsedTime({ min, variant = 'text', label, prefix, title, className }: ElapsedTimeProps) {
  if (min === null || min === undefined) return null
  const before = min < 0
  const text = before ? BEFORE_EARTHQUAKE_LABEL : label || fmtElapsed(min)
  if (!text) return null

  if (variant === 'text') {
    if (before) return null
    return (
      <span className={cn('whitespace-nowrap tabular-nums', className)} title={title ?? undefined}>
        {text}
      </span>
    )
  }

  return (
    <span
      title={title ?? undefined}
      className={cn(
        'inline-flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium tabular-nums',
        before ? 'bg-muted text-muted-foreground' : 'bg-needs-you-wash text-needs-you',
        className
      )}
    >
      <span
        aria-hidden='true'
        className={cn('h-2 w-2 rounded-full', before ? 'bg-muted-foreground' : 'bg-needs-you')}
      />
      {!before && prefix ? `${prefix} · ${text}` : text}
    </span>
  )
}
